'use client';
import React, { useRef, useState, useCallback } from 'react';

interface UploadZoneProps {
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  icon?: React.ReactNode;
  title?: string;
  subtitle?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export default function UploadZone({
  onFiles,
  accept,
  multiple = false,
  icon,
  title = 'Upload File',
  subtitle = 'Drag & drop or click to browse',
  hint,
  disabled = false,
  className = '',
  children,
}: UploadZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragActive(false);
      if (disabled) return;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        onFiles(e.dataTransfer.files);
        e.dataTransfer.clearData();
      }
    },
    [disabled, onFiles]
  );

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFiles(e.target.files);
    }
  };

  const defaultIcon = (
    <svg
      className={`w-8 h-8 transition-colors duration-200 ${isDragActive ? 'text-blue-600' : 'text-blue-400'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );

  return (
    <div
      className={`upload-zone-premium ${isDragActive ? 'drag-active' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
      aria-label={`${title}. ${subtitle}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />

      {children ?? (
        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
          {/* Icon wrapper with bounce on drag */}
          <div
            className={`
              w-16 h-16 rounded-2xl flex items-center justify-center mb-4 upload-icon-bounce
              transition-all duration-200
              ${isDragActive
                ? 'bg-blue-100 shadow-[0_0_0_6px_rgba(37,99,235,0.10)]'
                : 'bg-blue-50'}
            `}
          >
            {icon ?? defaultIcon}
          </div>

          <p
            className={`font-semibold mb-1 transition-colors duration-200 ${
              isDragActive ? 'text-blue-400' : 'text-text-primary'
            }`}
          >
            {isDragActive ? 'Drop to upload' : title}
          </p>
          <p className="text-text-secondary text-sm mb-2">{subtitle}</p>
          {hint && <p className="text-text-muted text-xs">{hint}</p>}

          {/* Drag indicator pill */}
          {isDragActive && (
            <div className="mt-3 px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold animate-bounce-in">
              Release to upload
            </div>
          )}
        </div>
      )}
    </div>
  );
}
